import { cn } from '../../utils/cn';

type EventStatusBadgeType = 'event' | 'project';

export interface EventStatusColorConfig {
  label: string;
  bg: string;
  text: string;
  border: string;
  tooltip: string;
}

/** Pipeline: idea → approved → scheduled → announced → in_progress → completed (+on_hold/cancelled). */
const EVENT_STATUS_CONFIG: Record<string, EventStatusColorConfig> = {
  idea: {
    bg: 'bg-slate-500/15',
    text: 'text-slate-300',
    border: 'border-slate-500/25',
    label: 'Idea',
    tooltip: 'Awaiting approval',
  },
  approved: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-300',
    border: 'border-blue-500/25',
    label: 'Approved',
    tooltip: 'Ready to schedule',
  },
  scheduled: {
    bg: 'bg-cyan-500/15',
    text: 'text-cyan-300',
    border: 'border-cyan-500/25',
    label: 'Scheduled',
    tooltip: 'A run window is on the calendar',
  },
  announced: {
    bg: 'bg-violet-500/15',
    text: 'text-violet-300',
    border: 'border-violet-500/25',
    label: 'Announced',
    tooltip: 'Publicly announced to players',
  },
  in_progress: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/25',
    label: 'In Progress',
    tooltip: 'The event is running right now',
  },
  completed: {
    bg: 'bg-green-500/15',
    text: 'text-green-300',
    border: 'border-green-500/25',
    label: 'Completed',
    tooltip: 'The event has finished',
  },
  on_hold: {
    bg: 'bg-yellow-500/15',
    text: 'text-yellow-300',
    border: 'border-yellow-500/25',
    label: 'On Hold',
    tooltip: 'Paused temporarily',
  },
  cancelled: {
    bg: 'bg-red-500/15',
    text: 'text-red-300',
    border: 'border-red-500/25',
    label: 'Cancelled',
    tooltip: 'Cancelled',
  },
};

const PROJECT_STATUS_CONFIG: Record<string, EventStatusColorConfig> = {
  planning: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-300',
    border: 'border-blue-500/25',
    label: 'Planning',
    tooltip: 'Project is in the planning phase',
  },
  in_progress: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/25',
    label: 'In Progress',
    tooltip: 'Active work is underway on this project',
  },
  review: {
    bg: 'bg-purple-500/15',
    text: 'text-purple-300',
    border: 'border-purple-500/25',
    label: 'Review',
    tooltip: 'Project is under team or lead review',
  },
  completed: {
    bg: 'bg-green-500/15',
    text: 'text-green-300',
    border: 'border-green-500/25',
    label: 'Completed',
    tooltip: 'Project has been finished',
  },
  on_hold: {
    bg: 'bg-yellow-500/15',
    text: 'text-yellow-300',
    border: 'border-yellow-500/25',
    label: 'On Hold',
    tooltip: 'Work is paused temporarily',
  },
  cancelled: {
    bg: 'bg-red-500/15',
    text: 'text-red-300',
    border: 'border-red-500/25',
    label: 'Cancelled',
    tooltip: 'Project was cancelled',
  },
};

interface EventStatusBadgeProps {
  status: string;
  type: EventStatusBadgeType;
  size?: 'sm' | 'md';
  className?: string;
}

export const getEventStatusColor = (
  status: string,
  type: EventStatusBadgeType,
): EventStatusColorConfig => {
  if (type === 'project') {
    return PROJECT_STATUS_CONFIG[status] ?? PROJECT_STATUS_CONFIG.planning;
  }
  return EVENT_STATUS_CONFIG[status] ?? EVENT_STATUS_CONFIG.idea;
};

export const EventStatusBadge = ({ status, type, size = 'sm', className }: EventStatusBadgeProps) => {
  const config = getEventStatusColor(status, type);

  return (
    <span
      className={cn(
        'badge badge-dot',
        config.bg,
        config.text,
        config.border,
        size === 'md' && 'text-xs px-2.5 py-1',
        className,
      )}
      title={config.tooltip}
    >
      {config.label}
    </span>
  );
};
